import { jwtDecode } from 'jwt-decode';
import api from './axios';
import { disconnectLogoutListener } from '../services/azurePubsubHandler';
import FileWithRag from '../services/FileWithRag';

export const decodeAccessToken = () => {
    try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token found");
        const decoded = jwtDecode(token);
        return decoded;
    } catch (error) {
        console.error("Error decoding access token:", error);
        return null;
    }
};

export const validateUserRole = (role) => {
    const userAuthDetails = decodeAccessToken();
    if (!userAuthDetails) return false;
    return userAuthDetails.role.toLowerCase() === role.toLowerCase();
};

export const deepEqual = (a, b) => {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;

    // Normalize array by sorting stringified items
    const sortedA = [...a].map(item => JSON.stringify(item, Object.keys(item).sort())).sort();
    const sortedB = [...b].map(item => JSON.stringify(item, Object.keys(item).sort())).sort();

    return sortedA.every((val, i) => val === sortedB[i]);
  }

  // Handle objects
  if (typeof a === "object" && typeof b === "object") {
    const keysA = Object.keys(a).sort();
    const keysB = Object.keys(b).sort();
    if (keysA.length !== keysB.length) return false;
    return keysA.every((key) => deepEqual(a[key], b[key]));
  }

  return false;
};


export const userSignOut = async () => {
  if (localStorage.getItem("token")) {
    await api.post("/auth/logout");
  }
  disconnectLogoutListener();
  localStorage.clear();
  window.location.reload();
}


export const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export const truncateString = (str = '', maxLength = 30) => {
  let limitCheck = maxLength + 3;
  if(!str) return str;
  return str.length > limitCheck ? str.slice(0, maxLength) + '...' : str;
}

export const areNumberArraysEqualAsSet = (a = [], b = []) => {
    if (a.length !== b.length) return false;

    const setA = new Set(a);
    for (const val of b) {
        if (!setA.has(val)) return false;
    }
    return true;
};


export function formatDisplayDate(dateString) {
  if (!dateString) return "";

  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function resolveAnalyticsDateRange({ startValue, endValue } = {}) {
  const defaults = getDefaultDates();

  const start = formatDateWithTZ(startValue, { pick: "start" });
  const endRaw = formatDateWithTZ(endValue, { pick: "end" });

  const start_date = start || defaults.start_date;
  const end_date = endRaw || start || defaults.end_date;

  return { start_date, end_date };
}

export function buildAnalyticsQueryParams({
  startValue,
  endValue,
  facilityId,
  extra = {},
} = {}) {
  const { start_date, end_date } = resolveAnalyticsDateRange({ startValue, endValue });

  const params = new URLSearchParams();
  if (start_date) params.append("start_date", start_date);
  if (end_date) params.append("end_date", end_date);
  if (facilityId) params.append("facility_id", facilityId);

  Object.entries(extra).forEach(([k, v]) => {
    if (v != null && v !== "") params.append(k, String(v));
  });

  return params.toString() ? `?${params.toString()}` : "";
}

export function formatDateWithTZ(value, { pick = "start" } = {}) {
  try {
    if (value == null || value === "") return "";

    if (Array.isArray(value)) {
      if (value.length === 0) return "";
      value = pick === "end" ? value[1] : value[0];
      if (!value) return "";
    }

    const date =
      value instanceof Date
        ? value
        : value?.startLocal instanceof Date
        ? value.startLocal
        : value?.endLocal instanceof Date
        ? value.endLocal
        : new Date(value);

    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      console.error("Invalid date passed to formatDateWithTZ:", value);
      return "";
    }

    return new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        0, 0, 0, 0
      )
    ).toISOString();
  } catch (error) {
    console.error("Error in formatDateWithTZ:", error);
    return "";
  }
}


// reference for fortmatting
// Supported Format Tokens

// | Token  | Meaning          | Example |
// | ------ | ---------------- | ------- |
// | `YYYY` | 4-digit year     | `2026`  |
// | `MMM`  | Short month name | `Feb`   |
// | `MM`   | Month (2-digit)  | `02`    |
// | `D`    | Day of month     | `2`     |
// | `DD`   | Day (2-digit)    | `02`    |
// | `HH`   | Hour (24h)       | `16`    |
// | `h`    | Hour (12h)       | `4`     |
// | `mm`   | Minutes          | `26`    |
// | `A`    | AM / PM          | `PM`    |


// examples

// formatUtcToLocal("2026-02-02T21:26:13.676095");
// // "Feb 2, 2026 4:26 PM"

// formatUtcToLocal(
//   "2026-02-02T21:26:13.676095",
//   "YYYY-MM-DD HH:mm"
// );
// // "2026-02-02 16:26"

// Date only
// formatUtcToLocal(created_at, "YYYY-MM-DD");

// Time only
// formatUtcToLocal(created_at, "h:mm A");


const DEFAULT_FORMAT = "MMM D, YYYY h:mm A";

export function formatUtcToLocal(utcDate, format = DEFAULT_FORMAT) {
  if (!utcDate) return "—";

  // Force UTC parsing
  const date = new Date(utcDate.endsWith("Z") ? utcDate : utcDate + "Z");

  if (isNaN(date.getTime())) return "—";

  return formatLocalDate(date, format);
}

function formatLocalDate(date, format) {
  const hours24 = date.getHours();
  const hours12 = hours24 % 12 || 12;

  const tokens = {
    YYYY: date.getFullYear(),
    MMM: date.toLocaleString(undefined, { month: "short" }),
    MM: String(date.getMonth() + 1).padStart(2, "0"),
    DD: String(date.getDate()).padStart(2, "0"),
    D: date.getDate(),
    HH: String(hours24).padStart(2, "0"),
    h: hours12,
    mm: String(date.getMinutes()).padStart(2, "0"),
    A: hours24 >= 12 ? "PM" : "AM",
  };

  return format.replace(
    /YYYY|MMM|MM|DD|D|HH|h|mm|A/g,
    (match) => tokens[match]
  );
}


export function displayFacilityName(facilityList, facility_id) {
  if (!Array.isArray(facilityList) || facilityList.length === 0) {
    return '';
  }
  const facility = facilityList.find(f => f.id === facility_id);
  return facility ? facility.name : '';
}
export function displayFacilityNameByFacilityNumber(facilityList, facility_number) {
  if (!Array.isArray(facilityList) || facilityList.length === 0) {
    return '';
  }
  const facility = facilityList.find(f => f.facility_number === facility_number);
  return facility ? facility.name : '';
}


export function displayUserName(fetchFacilityUserList, user_id) {
  if (!Array.isArray(fetchFacilityUserList) || fetchFacilityUserList.length === 0) {
    return '';
  }

  const user = fetchFacilityUserList.find(u => u.id === user_id);
  if (!user) {
    return '';
  }

  const firstName = user.first_name?.trim();
  const lastName = user.last_name?.trim();

  if (firstName || lastName) {
    return `${firstName ?? ''} ${lastName ?? ''}`.trim();
  }

  return user.email ?? '';
}

export const formatRole = (role) => {
  const r = role;
  if (r === "superadmin") return "Super Admin";
  if (r === "admin") return "Admin";
  if (r === "user") return "User";
  if (r === "owner") return "Owner";

  return role
    ? role
        .toString()
        .trim()
        .replace(/[_-]+/g, " ")    
        .replace(/\s+/g, " ")
        .split(" ")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ")
    : "";
};

export function getDefaultDates() {
  const today = new Date();

  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  return {
    start_date: formatDateWithTZ(sevenDaysAgo),
    end_date: formatDateWithTZ(today),
  };
}

// Shared shape for the comma-separated facility allowlist env vars: unset/empty
// disables the feature everywhere, "*" enables it for every facility.
function isFacilityInAllowlist(raw, facilityId) {
  if (!raw) return false;
  if (raw === "*") return true;

  const allowedFacilityIds = raw.split(",").map((id) => Number(id));
  return allowedFacilityIds.includes(Number(facilityId));
}

export function isFacilityEnrolledInAgentAnalytics(facilityId) {
  return isFacilityInAllowlist(
    process.env.REACT_APP_AGENT_ANALYTICS_FACILITIES,
    facilityId
  );
}

// Gates the dashboard's NEMS Buddy / Platform client filter. Only facilities
// that actually run the NEMS Buddy desktop shell have a meaningful split, so
// for everyone else the toggle is hidden and the KPIs stay all-clients.
export function isFacilityEnrolledInClientAppFilter(facilityId) {
  return isFacilityInAllowlist(
    process.env.REACT_APP_CLIENT_APP_FILTER_FACILITIES,
    facilityId
  );
}

// Label for the `desktop_buddy` client filter option when a facility has no
// override in REACT_APP_DESKTOP_BUDDY_LABELS.
export const DEFAULT_DESKTOP_BUDDY_LABEL = "Genserve Buddy";

// Display name for the `desktop_buddy` option at `facilityId`. Facilities run
// the desktop shell under their own brand (NEMS Buddy, …), so the label is
// configured per facility as a JSON map keyed by facility id — e.g.
// REACT_APP_DESKTOP_BUDDY_LABELS={"41": "NEMS Buddy"}. Anything unmapped, or a
// malformed value, shows the Genserve default rather than another tenant's brand.
export function getDesktopBuddyLabel(facilityId) {
  const raw = (process.env.REACT_APP_DESKTOP_BUDDY_LABELS ?? "").trim();
  if (!raw || facilityId == null) return DEFAULT_DESKTOP_BUDDY_LABEL;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULT_DESKTOP_BUDDY_LABEL;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return DEFAULT_DESKTOP_BUDDY_LABEL;
  }

  const label = parsed[String(facilityId)];
  return typeof label === "string" && label.trim()
    ? label.trim()
    : DEFAULT_DESKTOP_BUDDY_LABEL;
}

export function getDomain() {
  return { domain: window.location.origin };
}

export function getFormUrlEncoded(obj) {
  return Object.keys(obj)
    .map(
      (key) => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key])
    )
    .join("&");
}


export const getActualRagFilename = (url) => {
  if (!url || typeof url !== "string") {
    return "";
  }

  // 1. If it explicitly starts with "Web:", return as is
  if (url.startsWith("Web: ")) {
    return url;
  }

  const parts = url.split("/");
  const lastSegment = parts[parts.length - 1];

  // 2. SharePoint handling
  if (url.includes("sharepoint.com")) {
    try {
      const spUrl = new URL(url);

      if (spUrl.searchParams.has("file")) {
        return spUrl.searchParams.get("file") || "";
      } else {
        const nameWithoutExt = lastSegment.substring(
          0,
          lastSegment.lastIndexOf(".")
        );

        return (
          decodeURIComponent(
            nameWithoutExt || lastSegment
          ).replaceAll(" ", "_")
        );
      }
    } catch (err) {
      return "";
    }
  }

  // 3. UUID Pattern Check (Internal Files)
  const uuidPattern =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}_/;

  if (uuidPattern.test(lastSegment)) {
    // Slice off the 36-char UUID + underscore
    return lastSegment.slice(37);
  }

  // 4. Web URLs
  if (url.startsWith("http")) {
    try {
      const urlObj = new URL(url);
      return `${urlObj.hostname}${urlObj.pathname}`;
    } catch (e) {
      return url;
    }
  }

  return lastSegment;
};


export const previewRagFile =
  (fileUrl, sourceType = "file", setLoading) =>
  async (e) => {
    e.preventDefault();

    if (!fileUrl) return;

    let newTab = null;

    try {
      setLoading?.(fileUrl);

      // SharePoint → open directly
      if (fileUrl.toLowerCase().includes("sharepoint.com")) {
        window.open(fileUrl, "_blank");
        return;
      }

      const isInternalFile =
        fileUrl.includes("blob.core.windows.net") &&
        !fileUrl.includes("www.");

      // External link
      if (sourceType === "web" || !isInternalFile) {
        window.open(fileUrl, "_blank");
        return;
      }

      // 🚀 OPEN TAB IMMEDIATELY (prevents popup block)
      newTab = window.open("", "_blank");

      const payload = { blob_url: fileUrl };
      const response = await FileWithRag.getPublicUrlOfFile(payload);

      if (!response || !response.public_url) {
        newTab?.close();
        return;
      }

      const publicUrl = response.public_url;
      const fileExtension = publicUrl.split(".").pop()?.toLowerCase();

      if (fileExtension === "txt") {
        const res = await fetch(publicUrl);
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download =
          getActualRagFilename(fileUrl) ||
          publicUrl.split("/").pop() ||
          "file.txt";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(blobUrl);

        newTab?.close();
      } else {
        // Redirect already opened tab
        newTab.location.href = publicUrl;
      }
    } catch (err) {
      console.log("previewRagFile Error:", err);
      newTab?.close();
    } finally {
      setLoading?.(null);
    }
  };

